import { useState, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { discardStaging, confirmStaging } from './stagingApi';
import type { StagingReviewSummaryData } from './stagingApi';

interface StagingReviewActionsProps {
  adjustmentId: number;
  summary: StagingReviewSummaryData;
}

const StagingReviewActions = ({ adjustmentId, summary }: StagingReviewActionsProps) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [busy, setBusy] = useState<'confirm' | 'discard' | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const hasErrors = summary.errorCount > 0;
  const hasWarnings = summary.warningCount > 0;

  const handleConfirmClick = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    setConfirmDialogOpen(false);
    setBusy('confirm');
    try {
      const result = await confirmStaging(adjustmentId);
      notify(`Adjustment applied — ${result.confirmedCount} row(s) confirmed`, { type: 'success' });
      navigate('/resale/overrides');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm';
      notify(`Error: ${message}`, { type: 'error' });
    } finally {
      setBusy(null);
    }
  }, [adjustmentId, navigate, notify]);

  const handleDiscardClick = useCallback(() => {
    setDiscardDialogOpen(true);
  }, []);

  const handleDiscard = useCallback(async () => {
    setDiscardDialogOpen(false);
    setBusy('discard');
    try {
      const result = await discardStaging(adjustmentId);
      notify(`Discarded ${result.deletedCount} staged row(s)`, { type: 'info' });
      navigate('/resale/overrides');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to discard';
      notify(`Error: ${message}`, { type: 'error' });
    } finally {
      setBusy(null);
    }
  }, [adjustmentId, navigate, notify]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleDiscardClick}
          disabled={busy !== null}
          startIcon={busy === 'discard' ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {busy === 'discard' ? 'Discarding...' : 'Discard'}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmClick}
          disabled={hasErrors || busy !== null}
          startIcon={busy === 'confirm' ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {busy === 'confirm' ? 'Confirming...' : 'Confirm & Apply'}
        </Button>
      </Box>

      {/* Confirm dialog — extra warning if there are warning-status rows */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Pricing Adjustment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hasWarnings ?
              `${summary.warningCount} row(s) have warnings (overlapping criteria, zero inventory hits, etc.). Do you want to proceed anyway?` :
              `Apply ${summary.totalRows} row(s) to catalog pricing?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} autoFocus>
            {hasWarnings ? 'Confirm Anyway' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard dialog */}
      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
        <DialogTitle>Discard Staged Adjustment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete all 
            {' '}
            {summary.totalRows}
            {' '}
            staged row(s). This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDiscard} autoFocus>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StagingReviewActions;
